import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getProposalEntry, slidesForProposal } from '../components/proposal/proposalRegistry';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public read-only revisit of a proposal — no login.
 *
 * URL: /p/:revisitToken
 *
 * The client navigates the slides themselves (no broker driving). They can
 * still hit "Accept Proposal" on the final slide; that records acceptance
 * via the public revisit endpoint.
 */
const PublicRevisitPage = () => {
  const { revisitToken } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/public/proposals/${revisitToken}`);
        if (res.status === 410) throw new Error('This proposal link has expired.');
        if (!res.ok) throw new Error('Proposal not found.');
        const data = await res.json();
        if (cancelled) return;
        setProposal(data);
        if (data.status === 'accepted') setAccepted(true);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [revisitToken]);

  const onAccept = async () => {
    if (accepting || accepted) return;
    setAccepting(true);
    try {
      const res = await fetch(`${BACKEND}/api/public/proposals/${revisitToken}/accept`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed');
      setAccepted(true);
      toast.success('Proposal accepted — your broker will be in touch.');
    } catch (e) {
      toast.error('Could not record acceptance');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }
  if (error || !proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white p-6">
        <div className="text-center max-w-sm">
          <AlertTriangle className="h-10 w-10 mx-auto text-rose-500 mb-3" />
          <h2 className="text-xl font-semibold mb-1">Can't open this proposal</h2>
          <p className="text-slate-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const entry = getProposalEntry(proposal.type);
  const slides = slidesForProposal(proposal.type, proposal.payload);
  const max = slides.length - 1;

  return (
    <div className="relative h-screen flex flex-col bg-slate-100" data-testid="public-revisit">
      {/* Slide canvas — scrolls on mobile when content is taller than the
          viewport. flex-1 + min-h-0 is what makes the inner overflow-y-auto
          work inside a column-flex parent (otherwise it collapses to its
          natural height). */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {entry.render({
          index: Math.max(0, Math.min(max, slideIdx)),
          payload: proposal.payload,
          clientName: proposal.client_name,
          brokerName: proposal.broker_name,
          readonly: true,
          accepted,
          onAccept,
        })}
      </div>

      {/* Bottom slide nav */}
      <div className="bg-slate-950/95 backdrop-blur text-white px-3 sm:px-4 py-3 flex items-center justify-between gap-2 sm:gap-3 z-10 shrink-0">
        <Button
          onClick={() => setSlideIdx((i) => Math.max(0, i - 1))}
          disabled={slideIdx === 0}
          variant="outline"
          size="sm"
          className="bg-transparent border-white/20 hover:bg-white/10"
          data-testid="revisit-prev"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-1.5 overflow-x-auto px-2">
          {slides.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setSlideIdx(i)}
              className={`min-w-[28px] h-1.5 rounded-full transition-all ${
                i === slideIdx ? 'bg-emerald-400 w-8' : 'bg-white/20 hover:bg-white/40'
              }`}
              title={s.title}
            />
          ))}
          <span className="text-xs text-slate-400 ml-3 whitespace-nowrap">{slideIdx + 1} / {slides.length}</span>
        </div>
        <Button
          onClick={() => setSlideIdx((i) => Math.min(max, i + 1))}
          disabled={slideIdx === max}
          variant="outline"
          size="sm"
          className="bg-transparent border-white/20 hover:bg-white/10"
          data-testid="revisit-next"
        >
          Next <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default PublicRevisitPage;
